import React, { useEffect, useState } from "react";

// Build a URL-friendly slug: lowercase, non-alphanumerics collapsed to hyphens,
// trimmed of leading/trailing hyphens.
const slugify = (text) =>
  (text || "")
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[\s\W-]+/g, "-")
    .replace(/^-+|-+$/g, "");
import { Button } from "@mui/material";
import SoftBox from "components/SoftBox";
import PopUp from "./Component/PopUp";
import { useAppServices } from "hook/services";
import img_empty from "assets/images/img.jpg";
import deleteIcon from "../../../../../assets/themeIcons/delete-icon.svg";
import editIcon from "../../../../../assets/themeIcons/edit-icon.svg";
import { useAgencyInfo } from "context/agency";
import toast from "react-hot-toast";
import PrimaryButton from "../../../components/PrimaryButton";
import GhlMediaGallery from "components/GhlMediaGallery";
import { FaImages } from "react-icons/fa";
import "./accountDetailTabs.css";

// Format a Date to a value usable by <input type="date" /> (yyyy-mm-dd)
const toDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

// Human-friendly date for display, e.g. "Jun 15, 2026"
const toDisplayDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Coerce a price-like value to a finite number, or null when missing/invalid.
const toPrice = (value) => {
  if (value == null || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

// Percentage saved between regular and discounted price (rounded), or null.
const discountPercent = (regular, discounted) => {
  const reg = toPrice(regular);
  const disc = toPrice(discounted);
  if (reg == null || disc == null || reg <= 0 || disc >= reg) return null;
  return Math.round(((reg - disc) / reg) * 100);
};

// Lifecycle status for a deal based on its start/end dates.
const getDealStatus = (deal) => {
  const now = new Date();
  const start = deal.start_date ? new Date(deal.start_date) : null;
  const end = deal.end_date ? new Date(deal.end_date) : null;
  if (end && !isNaN(end.getTime()) && end < now) {
    return { label: "Expired", color: "#dc2626", bg: "#fef2f2" };
  }
  if (start && !isNaN(start.getTime()) && start > now) {
    return { label: "Scheduled", color: "#b45309", bg: "#fffbeb" };
  }
  return { label: "Active", color: "#16a34a", bg: "#f0fdf4" };
};

const DEALS_PER_PAGE = 8;

function Deals({ formData, handleRefresh }) {
  const AppService = useAppServices();
  const [agency] = useAgencyInfo();
  const [popup, setPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dealsData, setDealsData] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [image, setImage] = useState("");
  const [imageId, setImageId] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [coverImageId, setCoverImageId] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [slugError, setSlugError] = useState("");
  const [ghlGalleryOpen, setGhlGalleryOpen] = useState(false);
  // Which field the media gallery is currently picking for: "image" (card
  // thumbnail) or "cover_image" (detail-page hero).
  const [galleryTarget, setGalleryTarget] = useState("image");
  const [imageIdtarget, setImageIdtarget] = useState("image_id");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDeals, setTotalDeals] = useState(0);

  const account_id = formData?._id;
  const agency_id = agency?._id;

  const getDeals = async (targetPage = page) => {
    if (!account_id) return;
    const { response } = await AppService.deals.getbyaccount({
      query: `account_id=${account_id}&page=${targetPage}&limit=${DEALS_PER_PAGE}`,
    });
    if (response) {
      const data = response.data || [];
      const pages = response.pagination?.totalPages || 1;

      // If the requested page is now empty (e.g. after deleting the last item
      // on it), step back to the previous page instead of showing a blank grid.
      if (data.length === 0 && targetPage > 1) {
        return getDeals(targetPage - 1);
      }

      setDealsData(data);
      setTotalPages(pages);
      setTotalDeals(response.pagination?.total ?? data.length);
      setPage(targetPage);
    }
  };

  useEffect(() => {
    getDeals(1);
  }, [account_id]);

  const goToPage = (targetPage) => {
    if (targetPage < 1 || targetPage > totalPages || targetPage === page) return;
    getDeals(targetPage);
  };

  const openPopup = () => {
    setSelectedDeal(null);
    setImage("");
    setCoverImage("");
    setSlug("");
    setSlugEdited(false);
    setSlugError("");
    setPopup(true);
  };

  const closePopup = () => {
    setPopup(false);
    setSelectedDeal(null);
    setImage("");
    setCoverImage("");
    setSlug("");
    setSlugEdited(false);
    setSlugError("");
  };

  const openGallery = (target, imageId) => {
    setGalleryTarget(target);
    setImageIdtarget(imageId);
    setGhlGalleryOpen(true);
  };

  const handleGhlImageSelect = (pick) => {
    if (!pick?.url) return;
    const field = galleryTarget;
    const idField = imageIdtarget;
    if (selectedDeal) {
      setSelectedDeal((prev) => ({ ...prev, [field]: pick?.url, [idField]: pick?._id }));
    } else if (field === "cover_image") {
      setCoverImage(pick?.url);
      setCoverImageId(pick?._id);
    } else {
      setImage(pick?.url);
      setImageId(pick?._id);
    }
    toast.success("Image selected.");
  };

  const editDeal = (deal) => {
    setSelectedDeal(deal);
    setImage(deal.image || "");
    setCoverImage(deal.cover_image || "");
    setSlug(deal.slug || "");
    setSlugEdited(true);
    setSlugError("");
    setPopup(true);
  };

  const deleteDeal = async (deal) => {
    const { response } = await AppService.deals.delete({
      query: `id=${deal._id}`,
    });
    if (response) {
      handleRefresh();
      getDeals();
      toast.success("Deal deleted successfully");
    } else {
      toast.error("Error deleting deal");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const dealImage = selectedDeal?.image || image || "";
    const dealImageId = selectedDeal?.image_id || imageId || "";
    if (!dealImage) {
      toast.error("Please select an image for the deal");
      return;
    }

    // Optional hero image for the detail page; falls back to the card image
    // when not provided.
    const dealCoverImage = selectedDeal?.cover_image || coverImage || "";
    const dealCoverImageId = selectedDeal?.cover_image_id || coverImageId || "";

    const dealSlug = slugify(slug);
    if (!dealSlug) {
      setSlugError("Slug is required");
      return;
    }

    setSlugError("");
    setLoading(true);

    const payload = {
      title: e.target?.title?.value,
      slug: dealSlug,
      image: dealImage,
      image_id: dealImageId,
      cover_image: dealCoverImage,
      cover_image_id: dealCoverImageId,
      description: e.target?.description?.value || "",
      regular_price: e.target?.regular_price?.value || undefined,
      discounted_price: e.target?.discounted_price?.value || undefined,
      url: e.target?.url?.value || "",
      visit_offer_text: e.target?.visit_offer_text?.value || "",
      start_date: e.target?.start_date?.value || undefined,
      end_date: e.target?.end_date?.value || undefined,
      account_id,
      agency_id,
    };

    let response;
    if (selectedDeal) {
      response = await AppService.deals.update({
        payload: { ...payload, id: selectedDeal._id },
      });
    } else {
      response = await AppService.deals.create({ payload });
    }

    if (response?.response) {
      const wasEditing = Boolean(selectedDeal);
      handleRefresh();
      // New deals append to the end, so jump to the last page to reveal them;
      // edits stay on the current page.
      const nextTotalPages = wasEditing ? totalPages : Math.ceil((totalDeals + 1) / DEALS_PER_PAGE);
      getDeals(wasEditing ? page : nextTotalPages);
      closePopup();
      toast.success(wasEditing ? "Deal updated successfully!" : "New deal created successfully!");
    } else {
      const errData = response?.error;
      if (errData?.field === "slug") {
        setSlugError(
          errData?.message || "This slug is already in use. Please use a different one."
        );
      } else {
        toast.error(errData?.message || "Error saving deal");
      }
    }

    setLoading(false);
  };

  return (
    <div className="sd-account-detail-tab">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "12px",
          flexWrap: "wrap",
          padding: "0 10px",
          marginBottom: "16px",
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontWeight: 600, color: "#0f172a" }}>Deals</h3>
          <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#64748b" }}>
            {totalDeals
              ? `${totalDeals} deal${totalDeals === 1 ? "" : "s"} for this account`
              : "Create promotional offers for this account"}
          </p>
        </div>
        {totalDeals > 0 && (
          <PrimaryButton showPlus onClick={openPopup}>
            Add Deal
          </PrimaryButton>
        )}
      </div>

      {dealsData?.length === 0 ? (
        <SoftBox
          style={{
            margin: "10px",
            padding: "56px 24px",
            textAlign: "center",
            borderRadius: "14px",
            border: "1px dashed #cbd5e1",
            background: "#f8fafc",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              margin: "0 auto 16px",
              borderRadius: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#eef2ff",
              color: "#6366f1",
            }}
          >
            <FaImages size={26} />
          </div>
          <h4 style={{ margin: "0 0 6px", color: "#0f172a" }}>No deals yet</h4>
          <p style={{ margin: "0 0 18px", fontSize: "13px", color: "#64748b" }}>
            Add your first deal to showcase a special offer for this account.
          </p>
          <PrimaryButton showPlus onClick={openPopup}>
            Add Deal
          </PrimaryButton>
        </SoftBox>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "20px",
            padding: "0 10px",
          }}
        >
          {dealsData?.map((deal, index) => {
            const status = getDealStatus(deal);
            const percent = discountPercent(deal.regular_price, deal.discounted_price);
            const discounted = toPrice(deal.discounted_price);
            const regular = toPrice(deal.regular_price);
            return (
              <SoftBox
                key={deal._id || index}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "14px",
                  border: "1px solid #e9eef5",
                  background: "#fff",
                  overflow: "hidden",
                  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
                  transition: "transform 0.18s ease, box-shadow 0.18s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = "0 12px 24px rgba(15, 23, 42, 0.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 1px 3px rgba(15, 23, 42, 0.06)";
                }}
              >
                <div style={{ position: "relative" }}>
                  <img
                    src={deal.image || img_empty}
                    alt={deal.title}
                    style={{
                      width: "100%",
                      aspectRatio: "16 / 9",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  {percent != null && (
                    <span
                      style={{
                        position: "absolute",
                        top: "12px",
                        left: "12px",
                        background: "#dc2626",
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: 700,
                        padding: "4px 10px",
                        borderRadius: "999px",
                        boxShadow: "0 2px 6px rgba(220, 38, 38, 0.4)",
                      }}
                    >
                      {percent}% OFF
                    </span>
                  )}
                  <span
                    style={{
                      position: "absolute",
                      top: "12px",
                      right: "12px",
                      background: status.bg,
                      color: status.color,
                      fontSize: "11px",
                      fontWeight: 700,
                      padding: "4px 10px",
                      borderRadius: "999px",
                      letterSpacing: "0.02em",
                    }}
                  >
                    {status.label}
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    padding: "14px 16px 16px",
                    flex: 1,
                  }}
                >
                  <h4
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#0f172a",
                      lineHeight: 1.3,
                    }}
                  >
                    {deal.title}
                  </h4>

                  {deal.description && (
                    <p
                      style={{
                        margin: 0,
                        fontSize: "13px",
                        color: "#64748b",
                        lineHeight: 1.5,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {deal.description}
                    </p>
                  )}

                  {(discounted != null || regular != null) && (
                    <div style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
                      {discounted != null ? (
                        <>
                          <span style={{ fontWeight: 700, fontSize: "18px", color: "#16a34a" }}>
                            ${discounted}
                          </span>
                          {regular != null && (
                            <span
                              style={{
                                color: "#94a3b8",
                                fontSize: "14px",
                                textDecoration: "line-through",
                              }}
                            >
                              ${regular}
                            </span>
                          )}
                        </>
                      ) : (
                        <span style={{ fontWeight: 700, fontSize: "18px", color: "#0f172a" }}>
                          ${regular}
                        </span>
                      )}
                    </div>
                  )}

                  {(deal.start_date || deal.end_date) && (
                    <div style={{ fontSize: "12px", color: "#94a3b8" }}>
                      {toDisplayDate(deal.start_date)}
                      {deal.start_date && deal.end_date ? " — " : ""}
                      {toDisplayDate(deal.end_date)}
                    </div>
                  )}

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "10px",
                      marginTop: "auto",
                      paddingTop: "12px",
                      borderTop: "1px solid #f1f5f9",
                    }}
                  >
                    {deal.url ? (
                      <a
                        href={deal.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#1976d2",
                          textDecoration: "none",
                        }}
                      >
                        View Deal ↗
                      </a>
                    ) : (
                      <span />
                    )}
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Button
                        variant="contained"
                        color="white"
                        onClick={() => editDeal(deal)}
                        title="Edit deal"
                        style={{ minWidth: "auto", padding: "8px", boxShadow: "none" }}
                      >
                        <img src={editIcon} width={18} style={{ cursor: "pointer" }} alt="Edit" />
                      </Button>
                      <Button
                        variant="contained"
                        color="white"
                        title="Delete deal"
                        style={{
                          background: "#fef2f2",
                          minWidth: "auto",
                          padding: "8px",
                          boxShadow: "none",
                        }}
                        onClick={() => deleteDeal(deal)}
                      >
                        <img
                          src={deleteIcon}
                          width={18}
                          style={{ cursor: "pointer" }}
                          alt="Delete"
                        />
                      </Button>
                    </div>
                  </div>
                </div>
              </SoftBox>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            flexWrap: "wrap",
            padding: "24px 10px 8px",
          }}
        >
          <button
            type="button"
            onClick={() => goToPage(page - 1)}
            disabled={page <= 1}
            style={{
              minWidth: "36px",
              height: "36px",
              padding: "0 12px",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              background: "#fff",
              color: page <= 1 ? "#cbd5e1" : "#334155",
              cursor: page <= 1 ? "not-allowed" : "pointer",
              fontWeight: 600,
            }}
          >
            Prev
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
            const active = p === page;
            return (
              <button
                key={p}
                type="button"
                onClick={() => goToPage(p)}
                style={{
                  minWidth: "36px",
                  height: "36px",
                  borderRadius: "8px",
                  border: active ? "1px solid #1976d2" : "1px solid #e2e8f0",
                  background: active ? "#1976d2" : "#fff",
                  color: active ? "#fff" : "#334155",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                {p}
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => goToPage(page + 1)}
            disabled={page >= totalPages}
            style={{
              minWidth: "36px",
              height: "36px",
              padding: "0 12px",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              background: "#fff",
              color: page >= totalPages ? "#cbd5e1" : "#334155",
              cursor: page >= totalPages ? "not-allowed" : "pointer",
              fontWeight: 600,
            }}
          >
            Next
          </button>
        </div>
      )}

      <PopUp open={popup} handleClose={closePopup} title={selectedDeal ? "Edit Deal" : "New Deal"}>
        <form onSubmit={handleSubmit}>
          <div>
            <div style={{ display: "flex", width: "100%" }}>
              <div style={{ width: "50%", padding: "10px" }}>
                <SoftBox>
                  <label className="text-black">Title</label>
                  <input
                    className="form-control-agust"
                    name="title"
                    type="text"
                    placeholder="Title"
                    defaultValue={selectedDeal?.title || ""}
                    onChange={(e) => {
                      // Auto-fill the slug from the title until the user edits it
                      // manually, so they get a sensible default to tweak.
                      if (!slugEdited) setSlug(slugify(e.target.value));
                    }}
                    required
                  />
                </SoftBox>
              </div>
              <div style={{ width: "50%", padding: "10px" }}>
                <SoftBox>
                  <label className="text-black">Image (Card · 16:9)</label>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "6px" }}
                  >
                    <button
                      type="button"
                      onClick={() => openGallery("image", "image_id")}
                      title="Choose from Media"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                        color: "white",
                        border: "none",
                        padding: "10px 18px",
                        borderRadius: "10px",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        boxShadow: "0 2px 6px rgba(25, 118, 210, 0.35)",
                      }}
                    >
                      <FaImages size={16} />
                      {selectedDeal?.image || image ? "Change Image" : "Select from Media"}
                    </button>
                    {(selectedDeal?.image || image) && (
                      <img
                        src={selectedDeal?.image || image}
                        alt="Deal"
                        style={{
                          width: "128px",
                          aspectRatio: "16 / 9",
                          objectFit: "cover",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                        }}
                      />
                    )}
                  </div>
                </SoftBox>
              </div>
            </div>

            <div style={{ padding: "10px" }}>
              <SoftBox>
                <label className="text-black">Cover Image (Detail Page · 16:9)</label>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "6px" }}
                >
                  <button
                    type="button"
                    onClick={() => openGallery("cover_image", "cover_image_id")}
                    title="Choose from Media"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                      color: "white",
                      border: "none",
                      padding: "10px 18px",
                      borderRadius: "10px",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      boxShadow: "0 2px 6px rgba(25, 118, 210, 0.35)",
                    }}
                  >
                    <FaImages size={16} />
                    {selectedDeal?.cover_image || coverImage
                      ? "Change Cover Image"
                      : "Select from Media"}
                  </button>
                  {(selectedDeal?.cover_image || coverImage) && (
                    <img
                      src={selectedDeal?.cover_image || coverImage}
                      alt="Deal cover"
                      style={{
                        width: "160px",
                        aspectRatio: "16 / 9",
                        objectFit: "cover",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                      }}
                    />
                  )}
                </div>
                <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#94a3b8" }}>
                  Shown as the hero on the deal detail page. Leave empty to reuse the card image.
                </p>
              </SoftBox>
            </div>

            <div style={{ padding: "10px" }}>
              <SoftBox>
                <label className="text-black">Slug</label>
                <input
                  className="form-control-agust"
                  name="slug"
                  type="text"
                  placeholder="unique-deal-slug"
                  value={slug}
                  onChange={(e) => {
                    setSlugEdited(true);
                    setSlug(slugify(e.target.value));
                    if (slugError) setSlugError("");
                  }}
                  style={slugError ? { borderColor: "#dc2626", boxShadow: "none" } : undefined}
                  required
                />
                {slugError ? (
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: "12px",
                      color: "#dc2626",
                    }}
                  >
                    {slugError}
                  </p>
                ) : (
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: "12px",
                      color: "#94a3b8",
                    }}
                  >
                    Used in the deals public URL. Must be unique.
                  </p>
                )}
              </SoftBox>
            </div>

            <div style={{ display: "flex", width: "100%" }}>
              <div style={{ width: "50%", padding: "10px" }}>
                <SoftBox>
                  <label className="text-black">Regular Price</label>
                  <input
                    className="form-control-agust"
                    name="regular_price"
                    type="number"
                    step="any"
                    min="0"
                    placeholder="0.00"
                    defaultValue={selectedDeal?.regular_price ?? ""}
                  />
                </SoftBox>
              </div>
              <div style={{ width: "50%", padding: "10px" }}>
                <SoftBox>
                  <label className="text-black">Discounted Price</label>
                  <input
                    className="form-control-agust"
                    name="discounted_price"
                    type="number"
                    step="any"
                    min="0"
                    placeholder="0.00"
                    defaultValue={selectedDeal?.discounted_price ?? ""}
                  />
                </SoftBox>
              </div>
            </div>

            <div style={{ display: "flex", width: "100%" }}>
              <div style={{ width: "50%", padding: "10px" }}>
                <SoftBox>
                  <label className="text-black">Start Date</label>
                  <input
                    className="form-control-agust"
                    name="start_date"
                    type="date"
                    defaultValue={toDateInput(selectedDeal?.start_date)}
                  />
                </SoftBox>
              </div>
              <div style={{ width: "50%", padding: "10px" }}>
                <SoftBox>
                  <label className="text-black">End Date</label>
                  <input
                    className="form-control-agust"
                    name="end_date"
                    type="date"
                    defaultValue={toDateInput(selectedDeal?.end_date)}
                  />
                </SoftBox>
              </div>
            </div>
               
            <div style={{ display: "flex", width: "100%" }}>
            <div style={{ width:"50%", padding: "10px" }}>
              <SoftBox>
                <label className="text-black">URL</label>
                <input
                  className="form-control-agust"
                  name="url"
                  type="text"
                  placeholder="https://..."
                  defaultValue={selectedDeal?.url || ""}
                />
              </SoftBox>
            </div>
            <div style={{ width:"50%", padding: "10px" }}>
              <SoftBox>
                <label className="text-black">Visit Offer button text</label>
                <input
                  className="form-control-agust"
                  name="visit_offer_text"
                  type="text"
                  placeholder="Visit Offer"
                  defaultValue={selectedDeal?.visit_offer_text || ""}
                />
              </SoftBox>
            </div>
            </div>

            <div style={{ padding: "10px" }}>
              <SoftBox>
                <label className="text-black">Description</label>
                <textarea
                  className="form-control-agust"
                  name="description"
                  rows={4}
                  placeholder="Description"
                  defaultValue={selectedDeal?.description || ""}
                />
              </SoftBox>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "end", margin: "0 10px" }}>
            <PrimaryButton type="submit" disabled={loading}>
              {loading ? "Processing..." : selectedDeal ? "Update Deal" : "Save Deal"}
            </PrimaryButton>
          </div>
        </form>
      </PopUp>

      <GhlMediaGallery
        open={ghlGalleryOpen}
        onClose={() => setGhlGalleryOpen(false)}
        onSelect={handleGhlImageSelect}
        title={galleryTarget === "cover_image" ? "Select Cover Image" : "Select Deal Image"}
        enableCrop
        cropAspectRatio={16 / 9}
        googlePlaceId={formData?.google_place_id}
      />
    </div>
  );
}

export default Deals;
